import React, { useEffect } from "react";
import { connect } from "react-redux";
import OrganizationSelector from "./OrganizationSelector";
import { useWebServer } from "../../../providers";
import { fetchUserOrganizations } from "./../../../actions";
import { useLocation } from "react-router-dom";
import { getActiveOrg, setActiveOrg } from "../../../utils/activeOrg";

/**
 * Organization picker.
 *
 * The list comes from GET /organizations/user, which answers "which
 * organizations may this caller act as, in IDX". That is a backend question:
 * the memberships on the token carry only slugs, the display names live in this
 * product's database, and a superuser's list - any organization - is not on the
 * token at all. Asking the backend also means the picker can never offer
 * something the next request would be refused for.
 *
 * Shown whenever there is more than one to choose between. Someone with exactly
 * one has no choice to make, so a single-option dropdown would be noise.
 *
 * This used to switch organization by PATCHing the user's own Auth0
 * user_metadata.organization and re-minting the token. It no longer does. That
 * rewrote a stored fact about the user to express a per-session preference, it
 * could not represent "member of two organizations" at all, and it meant a
 * superuser looking at another tenant left a permanent trace on their own
 * account. The organization is now a per-request header; switching is local and
 * changes nothing server-side.
 */
const Index = ({ userOrganizations, capturing, fetchUserOrganizations }) => {
  const { sendRequest } = useWebServer();
  const { pathname } = useLocation();

  useEffect(() => {
    fetchUserOrganizations(sendRequest);
  }, [fetchUserOrganizations, sendRequest]);

  const active = getActiveOrg();

  if ((userOrganizations?.length || 0) < 2) return null;

  const onChangeOrganization = (event) => {
    const next = event.target.value;
    if (!next || next === "none" || next === active) return;

    setActiveOrg(next);

    // A full reload, rather than resetting redux.
    //
    // Nothing in this app reloads org-scoped data when the organization changes:
    // 18 reducers, no reset action, and no effect anywhere keyed on the
    // organization. Clearing them by hand means finding every fetch site, and a
    // missed one is silent - the user is left looking at the previous
    // organization's devices and trays. A reload is what the old switch did too,
    // via updateSuperUserOrganization, minus the logout and the metadata write.
    window.location.reload();
  };

  return (
    <OrganizationSelector
      organizationList={userOrganizations}
      selectedDevice={active || "none"}
      handleChangeOrganization={onChangeOrganization}
      loading={false}
      // Locked while a capture is running. Switching reloads the page, which
      // would abandon the capture mid-flight - and the tray it belongs to is in
      // the organization being navigated away from. The device selector is
      // disabled during a capture for the same reason.
      disabled={capturing}
      pathname={pathname}
    />
  );
};

const mapStateToProps = (state) => {
  const { capturing = false } = state.capture || {};
  const userOrganizations = [...(state.user?.userOrganizations || [])].sort(
    (a, b) => (a.display_name || "").localeCompare(b.display_name || "")
  );
  return {
    userOrganizations,
    capturing,
  };
};

export default connect(mapStateToProps, {
  fetchUserOrganizations,
})(Index);
